import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Sparkles,
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  ArrowRight,
} from 'lucide-react';
import { Card, CardContent, Button, Badge } from '../components/ui';
import { blogsApi } from '../lib/api';
import { useAuthStore } from '../stores/authStore';
import { formatRelativeTime, truncate } from '../lib/utils';
import { getStatusConfig } from '../lib/statusConfig';
import type { BlogJobStatusResponse } from '../types';

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: { value: number; label: string };
  color: string;
}> = ({ title, value, icon, trend, color }) => (
  <Card className="relative overflow-hidden">
    <CardContent className="p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">{title}</p>
          <p className="text-3xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
          {trend && (
            <div className="flex items-center gap-1 mt-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <span className="text-sm text-green-600 dark:text-green-400 font-medium">
                +{trend.value}%
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">{trend.label}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl ${color}`}>
          {icon}
        </div>
      </div>
    </CardContent>
    <div className={`absolute bottom-0 left-0 right-0 h-1 ${color.replace('bg-', 'bg-').replace('/10', '')}`} />
  </Card>
);

const RecentBlogCard: React.FC<{ job: BlogJobStatusResponse }> = ({ job }) => {
  const status = getStatusConfig(job.status);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group"
    >
      <Link to={`/blogs/${job.job_id}`}>
        <Card hover padding="none" className="overflow-hidden">
          <div className="p-5">
            <div className="flex items-start justify-between mb-3">
              <Badge variant={status.variant as any} icon={status.icon}>
                {status.label}
              </Badge>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatRelativeTime(job.created_at)}
              </span>
            </div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
              {truncate(job.selected_title || 'Untitled', 60)}
            </h3>
            {job.result && (
              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
                <span>{job.result.word_count || 0} words</span>
              </div>
            )}
          </div>
          {job.status === 'completed' && job.result && (
            <div className="px-5 py-3 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {job.result.word_count} words generated
              </span>
              <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors" />
            </div>
          )}
        </Card>
      </Link>
    </motion.div>
  );
};

export const Dashboard: React.FC = () => {
  const { user } = useAuthStore();
  const { data: jobsData, isLoading } = useQuery({
    queryKey: ['jobs', 'dashboard'],
    queryFn: () => blogsApi.getJobs(1, 10),
  });
  const jobs = jobsData?.items ?? [];

  const stats = {
    total: jobs.length,
    completed: jobs.filter((j) => j.status === 'completed').length,
    processing: jobs.filter((j) => j.status === 'processing' || j.status === 'pending').length,
    failed: jobs.filter((j) => j.status === 'failed').length,
  };

  const recentJobs = jobs.slice(0, 4);

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Section */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-primary-600 to-secondary-600 p-8 text-white">
        <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
        <div className="relative">
          <h1 className="text-3xl font-bold mb-2">
            Welcome back, {user?.first_name || 'Creator'}!
          </h1>
          <p className="text-white/80 mb-6 max-w-lg">
            Ready to create amazing content? Start generating high-quality blog posts with AI in just a few clicks.
          </p>
          <Link to="/generate">
            <Button
              variant="secondary"
              size="lg"
              leftIcon={<Sparkles className="h-5 w-5" />}
              className="bg-white text-primary-600 hover:bg-gray-100 shadow-xl"
            >
              Generate New Blog
            </Button>
          </Link>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Total Blogs"
          value={stats.total}
          icon={<FileText className="h-6 w-6 text-primary-600" />}
          color="bg-primary-100 dark:bg-primary-900/30"
        />
        <StatsCard
          title="Completed"
          value={stats.completed}
          icon={<CheckCircle className="h-6 w-6 text-green-600" />}
          trend={{ value: 12, label: 'this week' }}
          color="bg-green-100 dark:bg-green-900/30"
        />
        <StatsCard
          title="In Progress"
          value={stats.processing}
          icon={<Clock className="h-6 w-6 text-yellow-600" />}
          color="bg-yellow-100 dark:bg-yellow-900/30"
        />
        <StatsCard
          title="Failed"
          value={stats.failed}
          icon={<AlertCircle className="h-6 w-6 text-red-600" />}
          color="bg-red-100 dark:bg-red-900/30"
        />
      </div>

      {/* Recent Blogs */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Recent Blogs
          </h2>
          <Link to="/blogs">
            <Button variant="ghost" rightIcon={<ArrowRight className="h-4 w-4" />}>
              View all
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Card key={i} padding="none" className="animate-pulse">
                <div className="p-5 space-y-3">
                  <div className="h-5 w-20 skeleton rounded" />
                  <div className="h-6 w-3/4 skeleton rounded" />
                  <div className="flex gap-2">
                    <div className="h-5 w-16 skeleton rounded-full" />
                    <div className="h-5 w-16 skeleton rounded-full" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : recentJobs.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {recentJobs.map((job, index) => (
              <motion.div
                key={job.job_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <RecentBlogCard job={job} />
              </motion.div>
            ))}
          </div>
        ) : (
          <Card className="text-center py-12">
            <div className="flex flex-col items-center">
              <div className="p-4 rounded-full bg-gray-100 dark:bg-gray-800 mb-4">
                <FileText className="h-8 w-8 text-gray-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                No blogs yet
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-sm">
                Start by generating your first AI-powered blog post. It only takes a few clicks!
              </p>
              <Link to="/generate">
                <Button leftIcon={<Sparkles className="h-4 w-4" />}>
                  Generate Your First Blog
                </Button>
              </Link>
            </div>
          </Card>
        )}
      </div>

      {/* Quick Tips */}
      <Card className="bg-gradient-to-br from-secondary-50 to-primary-50 dark:from-secondary-900/20 dark:to-primary-900/20 border-0">
        <CardContent className="p-6">
          <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
            Quick Tips for Better Blogs
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                1
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">Use specific keywords</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  More specific keywords lead to more focused content
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                2
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">Define your audience</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Tailor content to your target readers
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold">
                3
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">Review and refine</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  AI content is a starting point - add your voice
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
